/**
 * @author Kelnik
 *
 * ImgMap - Карта на изображении
 */
import axios from 'axios';
import canvasTmpl from './templates/canvas.twig';
import Hammer from 'hammerjs';
import panelTmpl from './templates/panel/panel.twig';
import popupTmpl from './templates/popup/popup.twig';
import svgPanZoom from 'svg-pan-zoom';
import tooltipTmpl from './templates/tooltip/tooltip.twig';
import {Utils} from 'common/scripts/utils';

class ImgMap {
    constructor(options) {
        this.container = options.container;
        this.fullSelector = '.j-img-map-path-full';
        this.pathSelector = '.j-img-map-path';
        this.name = options.name;
        this.tooltipFixClass = 'img-map-tooltip-fix';
        this.defViewBoxWidth = 960;
        this.durationAnimation = 200;
        this.data = null;
    }

    async init() {
        await this._createDom();
        this._initElements();

        if (this.name === 'infrastructure-walks') {
            this._initLegend();
        }

        this._bindEvents();
    }

    async _createDom() {
        this.data = await this._getData();

        this.data.scale = this._calcSvgScale();

        if (this.name === 'infrastructure-walks') {
            this.data.themeColumns = true;
        }

        Utils.clearHtml(this.container);
        this.container.insertAdjacentHTML('beforeend', canvasTmpl(this.data));
    }

    async _getData() {
        const ajax = this.container.dataset.ajax;
        const base64 = this.container.dataset.json;
        let data = base64 ? JSON.parse(Utils.fromBase64(base64)) : '';

        if (ajax) {
            axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest';
            await axios.post(ajax).then((response) => {
                data = response.data;
            });
        }

        this.container.removeAttribute('data-json');

        return data;
    }

    _initElements() {
        this.paths = Array.from(this.container.querySelectorAll(this.pathSelector));
        this.fulls = Array.from(document.querySelectorAll(this.fullSelector));
        this.mapArea = this.container.querySelector('.j-img-map-svg');
    }

    _bindEvents() {
        this.paths.forEach((path) => {
            path.addEventListener('click', this._clickPath.bind(this, path));
        });

        Utils.clickOutside([this.pathSelector, `.${this.tooltipFixClass}`],
            this._clickPathOutside.bind(this), null, true);

        if (!Utils.isMobile() && (this.name === 'infrastructure-walks' || this.name === 'environment-special')) {
            this.paths.forEach((path) => {
                path.addEventListener('mouseenter', this._hoverPath.bind(this, path));
            });
        }

        this.fulls.forEach((full) => {
            if (full.dataset.nameImgMap === this.name && this.name === 'infrastructure-walks') {
                full.addEventListener('click', this._createInlineMap.bind(this));
            } else if (full.dataset.nameImgMap === this.name) {
                full.addEventListener('mouseover', this._createPopup.bind(this));
            }
        });

        window.addEventListener('resize', this._refreshCoordsTooltip.bind(this));
        window.addEventListener('scroll', this._refreshCoordsTooltip.bind(this));

        // если есть карта со ссылками на панорамы, принудительно открываем попап на мобилах
        this.fulls.forEach((full) => {
            if (full.classList.contains('infrastructure-walks')) {
                if (Utils.isMobile()) {
                    full.click();
                }
            }
        });
    }

    _calcSvgScale() {
        if (!this.data || !this.data.viewBox) {
            return 1;
        }

        const width = this.data.viewBox.split(' ')[2];

        return width / this.defViewBoxWidth;
    }

    _clickPath(path) {
        this.currentPath = path;
        this.currentData = this._getCurrentData(path, this.data);

        if (this.currentData.walk) {
            window.open(this.currentData.href, '_blank');

            return;
        }

        const coords = this._getTooltipCoords();

        if (this.tooltip) {
            const isMain = this.currentData.type === 'main-ext';

            this._setCoordsTooltip(coords, this.currentData.special, isMain);
            this._replaceDataTooltip(this.currentData);
        } else {
            this._createTooltip(coords, this.currentData);
        }
    }

    _hoverPath(path) {
        this.currentPath = path;
        this.currentData = this._getCurrentData(path, this.data);

        if (this.currentData.walk) {
            if (this.tooltip) {
                this._removeTooltip();
            }

            return;
        }

        const coords = this._getTooltipCoords();

        if (this.tooltip) {
            const isMain = this.currentData.type === 'main-ext';

            this._setCoordsTooltip(coords, this.currentData.special, isMain);
            this._replaceDataTooltip(this.currentData);
        } else {
            this._createTooltip(coords, this.currentData);
        }

        this._hoverLegendFromMap(path);
    }

    _getCurrentData(element, data) {
        return data.pins.find((pin) => {
            return element.id === `img-map-${pin.id}-${pin.x}-${pin.y}${pin.slug || ''}`;
        });
    }

    _clickPathOutside() {
        if (!this.tooltip) {
            return;
        }

        this._removeTooltip();
    }

    _getTooltipCoords() {
        if (!this.currentPath) {
            return null;
        }

        const rect = this.currentPath.getBoundingClientRect();

        return {
            x: rect.x,
            y: rect.y
        };
    }

    _createTooltip(coords, data) {
        this.tooltip = document.createElement('div');
        this.tooltip.classList.add(this.tooltipFixClass);

        document.body.append(this.tooltip);

        this._replaceDataTooltip(data);

        const isMain = data.type === 'main-ext';

        this._setCoordsTooltip(coords, data.special, isMain);
    }

    _setCoordsTooltip(coords, isSpecial = false, isMain = false) {
        let x = coords.x;
        let y = coords.y;

        if (!isSpecial) {
            if (!this.centerElement && this.currentData) {
                const selectorCenterElement =
                    this.currentData.img ? '.j-img-map-tooltip-img' : '.j-img-map-tooltip-pin';

                this.centerElement = this.tooltip.querySelector(selectorCenterElement);
            }

            x = this._getXCenterTooltipElement(x, this.centerElement);
            y = this._getYCenterTooltipImg(y, this.centerElement);
        } else {
            x = x + 14;
            const recTooltip = this.tooltip.getBoundingClientRect();

            if (isMain) {
                y = y - 50 - recTooltip.height;
            } else {
                y = y + 14 - recTooltip.height;
            }
        }

        this._setStyleCoordsTooltip(x, y);
        this._checkTooltipWindowBorder(x, y);
    }

    _setStyleCoordsTooltip(x, y) {
        this.tooltip.style.left = `${x}px`;
        this.tooltip.style.top = `${y}px`;
    }

    _getXCenterTooltipElement(x, element) {
        if (!element) {
            return x;
        }

        const leftTooltip = this.tooltip.getBoundingClientRect().left;
        const rect = element.getBoundingClientRect();
        const rectPath = this.currentPath.getBoundingClientRect();

        return x - ((rect.width / 2) + rect.left - leftTooltip - (rectPath.width / 2));
    }

    _getYCenterTooltipImg(y, element) {
        if (!element) {
            return y;
        }

        const rect = element.getBoundingClientRect();
        const topTooltip = this.tooltip.getBoundingClientRect().top;
        const rectPath = this.currentPath.getBoundingClientRect();

        return y - ((rect.height / 2) + rect.top - topTooltip - (rectPath.height / 2));
        // return y - (rect.height / 2 + rect.top - topTooltip);
    }

    _checkTooltipWindowBorder(x, y) {
        let xCoordinate = x;
        let yCoordinate = y;
        const offsetX = 25;
        const offsetY = 10;
        const recTooltip = this.tooltip.getBoundingClientRect();

        if (recTooltip.x < offsetX) {
            xCoordinate = offsetX;
        }

        if (recTooltip.x + recTooltip.width + offsetX > window.innerWidth) {
            xCoordinate = window.innerWidth - recTooltip.width - offsetX;
        }

        if (recTooltip.y < offsetX) {
            yCoordinate = offsetY;
        }

        if (recTooltip.y + recTooltip.height + offsetY > window.innerHeight) {
            yCoordinate = window.innerHeight - recTooltip.height - offsetY;
        }

        this._setStyleCoordsTooltip(xCoordinate, yCoordinate);
    }

    _replaceDataTooltip(data) {
        delete this.centerElement;
        Utils.clearHtml(this.tooltip);
        this.tooltip.insertAdjacentHTML('beforeend', tooltipTmpl(data));

        this._bindEventsAfterCreateTooltip();
    }

    // eslint-disable-next-line
    _refreshCoordsTooltip() {
        if (!this.currentPath) {
            return null;
        }

        const coords = this._getTooltipCoords();

        if (!this._checkPinInViewport()) {
            this._removeTooltip();

            // eslint-disable-next-line
            return;
        }

        this._setCoordsTooltip(coords);
    }

    _checkPinInViewport() {
        return Utils.isInViewport(this.currentPath);
    }

    _checkMapInViewport() {
        return Utils.isInViewport(this.container);
    }

    _bindEventsAfterCreateTooltip() {
        const close = this.tooltip.querySelector('.j-img-map-tooltip-close');

        close.addEventListener('click', this._removeTooltip.bind(this));
    }

    _removeTooltip() {
        if (!this.tooltip) {
            return;
        }

        Array.from(this.tooltip.children).forEach((child) => {
            child.classList.add('is-hide');
        });

        setTimeout(() => {
            Utils.removeElement(this.tooltip);
            delete this.tooltip;
            delete this.currentPath;
            delete this.centerElement;
        }, this.durationAnimation);

        if (!Utils.isMobile()) {
            this._unhoverLegend();
        }
    }

    _createPopup() {
        this.dataPopup = this._copyDeepObject(this.data);
        this.addSlugsInPins();

        this.popup = document.createElement('div');
        this.popup.classList.add('img-map-popup');

        this.popup.insertAdjacentHTML('beforeend', popupTmpl(this.dataPopup));

        document.body.append(this.popup);

        Utils.bodyFixed(this.popup);

        this._initSvgPanZoom();
        this._bindEventsAfterCreatePopup();
    }

    _createInlineMap() {
        const wrapper = document.querySelector('.inline-map-container');

        if (this.name !== 'infrastructure-walks') {
            return;
        }

        this.dataPopup = this._copyDeepObject(this.data);
        this.addSlugsInPins();

        this.popup = document.createElement('div');
        this.popup.classList.add('infrastructure__map-mobile');
        this.popup.classList.add('img-map-popup');
        this.popup.insertAdjacentHTML('beforeend', popupTmpl(this.dataPopup));

        if (wrapper.innerHTML) {
            return;
        }

        wrapper.append(this.popup);

        this._initSvgPanZoom();
        this._bindEventsAfterCreatePopup();
    }

    addSlugsInPins() {
        if (!this.dataPopup || !this.dataPopup.pins) {
            return;
        }

        this.dataPopup.pins.forEach((pin) => {
            pin.slug = 'popup';
        });
    }

    _bindEventsAfterCreatePopup() {
        if (!this.popup) {
            return;
        }

        const close = this.popup.querySelector('.j-img-map-popup-close');
        const minus = this.popup.querySelector('.j-img-map-popup-minus');
        const plus = this.popup.querySelector('.j-img-map-popup-plus');
        const pins = Array.from(this.popup.querySelectorAll('.j-img-map-path'));
        const legendBtn = this.popup.querySelector('.j-img-map-popup-legend-btn');
        const legendContainer = this.popup.querySelector('.j-img-map-popup-legend');

        plus.addEventListener('click', this._zoomIn.bind(this));
        minus.addEventListener('click', this._zoomOut.bind(this));
        close.addEventListener('click', this._removePopup.bind(this));
        pins.forEach((pin) => {
            const pinHammer = new Hammer(pin);

            pinHammer.on('tap', this._createPanel.bind(this, pin));
        });

        if (legendBtn) {
            legendBtn.addEventListener('click', this._openLegend.bind(this, legendBtn, legendContainer));
        }
    }

    _initSvgPanZoom() {
        const svg = this.popup.querySelector('.j-img-map-svg');

        this.svgPanZoom = svgPanZoom(svg, {
            zoomScaleSensitivity: 0.5,
            minZoom             : 0.7,
            maxZoom             : 5,
            customEventsHandler : {
                haltEventListeners: ['touchstart', 'touchend', 'touchmove', 'touchleave', 'touchcancel'],
                init              : this._customEventsHandlerInit.bind(this),
                destroy           : () => {
                    this.hammerPinch.destroy();
                }
            }
        });
    }

    _customEventsHandlerInit(options) {
        if (this.name === 'infrastructure-walks') {
            document.addEventListener('click', (evt) => {
                this._tapOnSvg(evt);
            });
        }

        const instance = options.instance;
        let initialScale = 1;
        let pannedX = 0;
        let pannedY = 0;

        this.hammerPinch = new Hammer(options.svgElement);
        this.hammerPinch.get('pinch').set({enable: true});
        this.hammerPinch.on('doubletap', () => {
            instance.zoomIn();
        });

        this.hammerPinch.on('tap', this._tapOnSvg.bind(this));

        this.hammerPinch.on('panstart panmove', (event) => {
            if (event.type === 'panstart') {
                pannedX = 0;
                pannedY = 0;
            }

            instance.panBy({
                x: event.deltaX - pannedX,
                y: event.deltaY - pannedY
            });

            pannedX = event.deltaX;
            pannedY = event.deltaY;
        });

        this.hammerPinch.on('pinchstart pinchmove', (event) => {
            if (event.type === 'pinchstart') {
                initialScale = instance.getZoom();
                instance.zoomAtPoint(initialScale * event.scale, {
                    x: event.center.x,
                    y: event.center.y
                });
            }

            instance.zoomAtPoint(initialScale * event.scale, {
                x: event.center.x,
                y: event.center.y
            });
        });

        options.svgElement.addEventListener('touchmove', (event) => {
            event.preventDefault();
        });
    }

    _tapOnSvg(event) {
        const target = event.target;
        const parentPin = Utils.closest(target, this.pathSelector);

        if (!parentPin) {
            this._removePanel();
        }
    }

    _zoomIn() {
        if (this.svgPanZoom) {
            this.svgPanZoom.zoomIn();
        }
    }

    _zoomOut() {
        if (this.svgPanZoom) {
            this.svgPanZoom.zoomOut();
        }
    }

    _removePopup() {
        if (!this.popup) {
            return;
        }

        Utils.bodyStatic();
        Utils.removeElement(this.popup);

        if (this.panel) {
            this._removePanel();
        }

        delete this.popup;
        delete this.svgPanZoom;
    }

    _openLegend(button, container) {
        button.classList.toggle('is-open');
        container.classList.toggle('is-open');
    }

    _copyDeepObject(object) {
        return JSON.parse(JSON.stringify(object));
    }

    _createPanel(pin) {
        const currentDataPanel = this._getCurrentData(pin, this.dataPopup);

        currentDataPanel.isWalks = this.name === 'infrastructure-walks';

        if (currentDataPanel.walk) {
            window.open(currentDataPanel.href, '_blank');
            this._removePanel();

            return;
        }

        if (this.currentDataPanel && this.currentDataPanel.id === currentDataPanel.id) {
            this._removePanel();

            return;
        }

        this.currentDataPanel = currentDataPanel;

        if (!this.panel) {
            this.panel = document.createElement('div');
            this.panel.classList.add('img-map-panel');
        }

        Utils.clearHtml(this.panel);
        this.panel.insertAdjacentHTML('beforeend', panelTmpl(currentDataPanel));
        document.body.append(this.panel);

        this._bindEventsAfterCreatePanel();
    }

    _bindEventsAfterCreatePanel() {
        if (!this.panel) {
            return;
        }

        const panelHammer = new Hammer(this.panel);

        panelHammer.get('swipe').set({direction: Hammer.DIRECTION_ALL});
        panelHammer.on('swipedown', this._removePanel.bind(this));

        window.addEventListener('scroll', () => {
            const isMapInViewport = this._checkMapInViewport();

            if (!isMapInViewport) {
                this._removePanel();
            }
        });
    }

    _removePanel() {
        if (this.panel) {
            Utils.removeElement(this.panel);
        }

        delete this.panel;
        delete this.currentDataPanel;
    }

    _initLegend() {
        this.legend = this.container.querySelector('.j-img-map-popup-legend');
        this.legendItems = Array.from(this.container.querySelectorAll('.j-img-map-legend-link'));

        if (!this.legendItems || !this.legend) return;

        this.legendItems.forEach((item) => {
            item.addEventListener('mouseenter', this._hoverLegend.bind(this, item));
        });

        this.legend.addEventListener('mouseleave', this._unhoverLegend.bind(this));
    }

    _hoverLegend(item) {
        if (Utils.isMobile()) {
            return;
        }

        if (this.tooltip) {
            this._removeTooltip();
        }

        const currentCategory = item.innerText;
        const currentIds = this._filterByCategory(currentCategory);

        if (currentIds.length) {
            this.paths.forEach((path) => {
                const currentId = path.id.split('-')[2];

                if (currentIds.includes(Number(currentId))) {
                    path.style.opacity = '1';
                } else {
                    path.style.opacity = '0.6';
                }
            });
        }
    }

    _unhoverLegend() {
        if (this.legendItems) {
            this.legendItems.forEach((item) => {
                if (item.style.transform) {
                    item.style.removeProperty('transform');
                }
            });
        }

        this.paths.forEach((path) => {
            path.style.opacity = '1';
        });
    }

    _hoverLegendFromMap(path) {
        const currentId = path.id.split('-')[2];
        const currentCategory = this._findCategoryById(currentId);

        if (!this.legendItems) return;

        this.legendItems.forEach((item) => {
            const pinText = item.innerText.toLowerCase();

            if (pinText.includes(currentCategory.toLowerCase())) {
                item.style.transform = 'scale(1.15)';
            } else {
                item.style.transform = 'none';
            }
        });
    }

    _filterByCategory(category) {
        const result = [];

        this.data.pins.forEach((pin) => {
            if (pin.title === category) {
                result.push(pin.id);
            }
        });

        return result;
    }

    _findCategoryById(id) {
        let result = null;

        this.data.pins.forEach((pin) => {
            if (pin.id === Number(id)) {
                result = pin.title;
            }
        });

        return result;
    }
}

export default ImgMap;
